import { useState } from 'react';
import { useAsync } from 'react-use-v13';
import API from '@scale/frontend-lib/dashboard-api';
import { FieldValidation } from '@scale/shared/types/rlhf';
import { LintSeverity } from '@scale/shared/task_lint/task_linter';

type FieldValidationResults = {
  [key: string]: FieldValidation[];
};

interface FieldValidationHook {
  fieldValidationEnabled: boolean;
  fieldIdsToValidate: string[];
  validateField: (fieldResponse: string) => Promise<boolean>;
  fieldValidationResults: FieldValidationResults;
  fieldIdPendingValidation: string | undefined;
  setFieldIdPendingValidation: (fieldIdPendingValidation: string | undefined) => void;
}

export const useFieldValidation = (
  projectId: string | undefined,
  assignmentId: string | undefined,
): FieldValidationHook => {
  const [fieldValidationEnabled, setFieldValidationEnabled] = useState(false);
  const [fieldIdsToValidate, setFieldIdsToValidate] = useState<string[]>([]);
  const [fieldValidationResults, setFieldValidationResults] = useState<FieldValidationResults>({});
  const [fieldIdPendingValidation, setFieldIdPendingValidation] = useState<string | undefined>(
    undefined,
  );

  useAsync(async () => {
    if (!projectId || !assignmentId) return;
    const fieldValidationConfig = await API.selfserve.fetchFieldValidationConfig({
      projectId,
    });
    setFieldValidationEnabled(!!fieldValidationConfig.enabled);
    if (fieldValidationConfig.enabled) {
      setFieldIdsToValidate(fieldValidationConfig.fieldIdsToValidate ?? []);
    }
  }, [projectId, assignmentId]);

  const validateField = async (fieldResponse: string) => {
    if (!projectId || !fieldIdPendingValidation || !assignmentId) return false;
    const { fieldValidationResult } = await API.selfserve.fieldValidation({
      projectId,
      fieldId: fieldIdPendingValidation,
      fieldResponse,
      assignmentId,
    });

    setFieldValidationResults({
      ...fieldValidationResults,
      [fieldIdPendingValidation]: fieldValidationResult,
    });
    return (
      (fieldValidationResult ?? []).filter(fv => fv.severity === LintSeverity.ERROR).length === 0
    );
  };

  return {
    fieldValidationEnabled,
    fieldIdsToValidate,
    validateField,
    fieldValidationResults,
    fieldIdPendingValidation,
    setFieldIdPendingValidation,
  };
};
